/**
 * Staff Hub and HR (WP-HR): "My Hub" for every employee (`/me`) and
 * "Team & HR" for managers (Settings > Team & HR; Dentrix: Office Manager >
 * Maintenance > Practice Setup > Staff / Employees).
 *
 * Never part of these shapes, by design: social security numbers, bank
 * details, W-4 / tax data and pay stubs. Payroll stays in Square Payroll;
 * Velora shows hours and links to Square for stubs (Square has no pay-stub
 * API, so nothing is copied).
 */
import { z } from 'zod';

export const HR_ENTITY = 'hr';

export const HR_PERMISSIONS = {
  self: 'hr.self',
  view: 'hr.view',
  manage: 'hr.manage',
  sensitive: 'hr.view_sensitive',
} as const;

const IsoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'YYYY-MM-DD');
const Instant = z.string();
const Uuid = z.uuid();

/* ------------------------------------------------------------------------ */
/* Michigan credential catalogue (LARA, R 338.11701 / R 338.11704)           */
/* ------------------------------------------------------------------------ */

/** Licensed roles the catalogue knows; Velora role keys map onto them. */
export const CREDENTIAL_ROLES = ['dentist', 'hygienist', 'assistant'] as const;
export type CredentialRole = (typeof CREDENTIAL_ROLES)[number];
export const CredentialRoleSchema = z.enum(CREDENTIAL_ROLES);

export interface CredentialTypeDef {
  key: string;
  label: string;
  /** Short column header for the compliance grid. */
  short: string;
  /** Renewal cycle; null for one-time items (no expiry). */
  cycleMonths: number | null;
  /** Licensed roles this applies to. */
  roles: readonly CredentialRole[];
  /** Required for every person in those roles (else optional, tracked when held). */
  required: boolean;
  /** Rule source and caveats, shown in the hub. */
  note: string;
  /** CE hours required per renewal cycle (licenses only). */
  ceHours?: number;
}

/**
 * Seeded for Michigan. Confirm each person's real expiry in MiPLUS: sources
 * disagree on whether it follows the licensure date or the birthday.
 */
export const CREDENTIAL_TYPES: readonly CredentialTypeDef[] = [
  {
    key: 'dds',
    label: 'Dentist license (Michigan)',
    short: 'DDS',
    cycleMonths: 36,
    roles: ['dentist'],
    required: true,
    note: 'Three-year cycle; 60 hours of CE (R 338.11701).',
    ceHours: 60,
  },
  {
    key: 'rdh',
    label: 'Registered dental hygienist license (Michigan)',
    short: 'RDH',
    cycleMonths: 36,
    roles: ['hygienist'],
    required: true,
    note: 'Three-year cycle; 36 hours of CE (R 338.11704).',
    ceHours: 36,
  },
  {
    key: 'rda',
    label: 'Registered dental assistant license (Michigan)',
    short: 'RDA',
    cycleMonths: 36,
    roles: ['assistant'],
    required: false,
    note: 'Three-year cycle; 36 hours of CE (R 338.11704). Only for assistants who hold the RDA.',
    ceHours: 36,
  },
  {
    key: 'mi_cs',
    label: 'Michigan controlled substance license',
    short: 'MI CS',
    cycleMonths: 36,
    roles: ['dentist'],
    required: true,
    note: 'Renews with the dental license (R 338.3135).',
  },
  {
    key: 'dea',
    label: 'DEA registration',
    short: 'DEA',
    cycleMonths: 36,
    roles: ['dentist'],
    required: true,
    note: 'Federal, three-year cycle.',
  },
  {
    key: 'bls',
    label: 'BLS / CPR card (hands-on)',
    short: 'BLS',
    cycleMonths: 24,
    roles: ['dentist', 'hygienist', 'assistant'],
    required: true,
    note: 'Current BLS or ACLS with a hands-on part is a condition of renewal.',
  },
  {
    key: 'opioid_training',
    label: 'Opioid and controlled-substance prescribing training',
    short: 'Opioid',
    cycleMonths: null,
    roles: ['dentist'],
    required: true,
    note: 'R 338.3135 (amended 2024). Whether it repeats each cycle is unconfirmed; keep proof for 3 cycles plus 1 year.',
  },
  {
    key: 'dea_mate',
    label: 'DEA MATE Act training (8 hours)',
    short: 'MATE',
    cycleMonths: null,
    roles: ['dentist'],
    required: true,
    note: 'One-time federal requirement for DEA registrants.',
  },
  {
    key: 'implicit_bias',
    label: 'Implicit bias training',
    short: 'Bias',
    cycleMonths: 12,
    roles: ['dentist', 'hygienist', 'assistant'],
    required: true,
    note: 'R 338.7004: 1 hour a year of the cycle. Gives no dentistry CE credit; 2025 repeal bills are unresolved.',
  },
  {
    key: 'human_trafficking',
    label: 'Human trafficking training',
    short: 'HT',
    cycleMonths: null,
    roles: ['dentist', 'hygienist', 'assistant'],
    required: true,
    note: 'One time (MCL 333.16148).',
  },
  {
    key: 'rdh_la',
    label: 'RDH local anesthesia',
    short: 'RDH LA',
    cycleMonths: null,
    roles: ['hygienist'],
    required: false,
    note: '15 h didactic and 14 h clinical plus the exam within 18 months (MCL 333.16611). Adults only.',
  },
  {
    key: 'rdh_n2o',
    label: 'RDH nitrous oxide',
    short: 'RDH N2O',
    cycleMonths: null,
    roles: ['hygienist'],
    required: false,
    note: '4 h didactic and 4 h clinical plus the exam.',
  },
  {
    key: 'rda_n2o',
    label: 'RDA nitrous oxide monitoring',
    short: 'RDA N2O',
    cycleMonths: null,
    roles: ['assistant'],
    required: false,
    note: 'Assist and monitor only, after a course of at least 5 hours.',
  },
  {
    key: 'radiography',
    label: 'Radiography course',
    short: 'X-ray',
    cycleMonths: null,
    roles: ['assistant'],
    required: false,
    note: 'Board-equivalent course; no separate permit (R 338.11411).',
  },
];

export const CREDENTIAL_TYPE_KEYS = CREDENTIAL_TYPES.map((t) => t.key);
export const CredentialTypeKeySchema = z
  .string()
  .refine((k) => CREDENTIAL_TYPE_KEYS.includes(k), 'unknown credential type');

export function credentialType(key: string): CredentialTypeDef | undefined {
  return CREDENTIAL_TYPES.find((t) => t.key === key);
}

/** Days before expiry when staff are alerted (0 = on the day it expires). */
export const STAFF_ALERT_DAYS = [90, 60, 30, 0] as const;
/** Days before expiry when managers are alerted. */
export const MANAGER_ALERT_DAYS = [60, 30, 0] as const;

/* ------------------------------------------------------------------------ */
/* Status                                                                    */
/* ------------------------------------------------------------------------ */

/**
 * Compliance tone for one cell or card: `ok` current, `soon` inside 90 days,
 * `due` inside 30 days, `overdue` expired or missing a required item,
 * `pending` held but not verified by a manager, `na` not applicable.
 */
export const COMPLIANCE_TONES = ['ok', 'soon', 'due', 'overdue', 'pending', 'na'] as const;
export type ComplianceTone = (typeof COMPLIANCE_TONES)[number];
export const ComplianceToneSchema = z.enum(COMPLIANCE_TONES);

export const CredentialStatusSchema = z.object({
  tone: ComplianceToneSchema,
  /** Whole days until expiry (negative once expired); null without an expiry. */
  daysLeft: z.number().int().nullable(),
  label: z.string(),
});
export type CredentialStatus = z.infer<typeof CredentialStatusSchema>;

/* ------------------------------------------------------------------------ */
/* Profile                                                                   */
/* ------------------------------------------------------------------------ */

export const EMPLOYMENT_TYPES = ['full_time', 'part_time', 'contractor', 'temporary'] as const;
export const EmploymentTypeSchema = z.enum(EMPLOYMENT_TYPES);

export const StaffProfileSchema = z.object({
  userId: Uuid,
  name: z.string(),
  email: z.string().nullable(),
  /** Velora role names (Front Desk, Hygienist, ...). */
  roles: z.array(z.string()),
  /** Licensed roles the catalogue applies (from the Velora role and the profile). */
  credentialRoles: z.array(CredentialRoleSchema),
  /** Licensed roles added on the profile beyond the Velora role. */
  extraCredentialRoles: z.array(CredentialRoleSchema),
  title: z.string().nullable(),
  hireDate: IsoDate.nullable(),
  endDate: IsoDate.nullable(),
  employmentType: EmploymentTypeSchema.nullable(),
  emergency: z.object({
    name: z.string().nullable(),
    phone: z.string().nullable(),
    relation: z.string().nullable(),
  }),
  officeIds: z.array(Uuid),
  active: z.boolean(),
});
export type StaffProfile = z.infer<typeof StaffProfileSchema>;

/** What a person edits on their own profile. */
export const SelfProfileBody = z
  .object({
    emergency: z.object({
      name: z.string().trim().max(120).nullable(),
      phone: z
        .string()
        .trim()
        .regex(/^[0-9+() .-]{7,25}$/, 'Enter a phone number')
        .nullable(),
      relation: z.string().trim().max(40).nullable(),
    }),
  })
  .strict();
export type SelfProfileBody = z.infer<typeof SelfProfileBody>;

/** What a manager edits. */
export const ManagerProfileBody = z
  .object({
    title: z.string().trim().min(1).max(80).nullable().optional(),
    hireDate: IsoDate.nullable().optional(),
    endDate: IsoDate.nullable().optional(),
    employmentType: EmploymentTypeSchema.nullable().optional(),
    extraCredentialRoles: z.array(CredentialRoleSchema).max(3).optional(),
  })
  .strict();
export type ManagerProfileBody = z.infer<typeof ManagerProfileBody>;

/* ------------------------------------------------------------------------ */
/* Credentials                                                               */
/* ------------------------------------------------------------------------ */

export const CredentialSchema = z.object({
  id: Uuid,
  userId: Uuid,
  typeKey: z.string(),
  typeLabel: z.string(),
  number: z.string().nullable(),
  state: z.string(),
  issuedOn: IsoDate.nullable(),
  expiresOn: IsoDate.nullable(),
  documentId: Uuid.nullable(),
  verified: z.object({ byName: z.string(), at: Instant }).nullable(),
  note: z.string().nullable(),
  status: CredentialStatusSchema,
});
export type Credential = z.infer<typeof CredentialSchema>;

export const CredentialBody = z
  .object({
    typeKey: CredentialTypeKeySchema,
    number: z.string().trim().min(1).max(64).nullable().optional(),
    state: z
      .string()
      .regex(/^[A-Z]{2}$/)
      .optional(),
    issuedOn: IsoDate.nullable().optional(),
    expiresOn: IsoDate.nullable().optional(),
    note: z.string().trim().max(500).nullable().optional(),
  })
  .strict();
export type CredentialBody = z.infer<typeof CredentialBody>;

export const CredentialPatchBody = CredentialBody.omit({ typeKey: true }).strict();
export type CredentialPatchBody = z.infer<typeof CredentialPatchBody>;

/** A required item the person does not have on file yet. */
export const MissingCredentialSchema = z.object({
  typeKey: z.string(),
  typeLabel: z.string(),
  note: z.string(),
});

export const MyCredentialsSchema = z.object({
  credentials: z.array(CredentialSchema),
  missing: z.array(MissingCredentialSchema),
  /** Catalogue entries this person may add (their roles), required first. */
  available: z.array(
    z.object({
      key: z.string(),
      label: z.string(),
      required: z.boolean(),
      oneTime: z.boolean(),
      note: z.string(),
    }),
  ),
});
export type MyCredentials = z.infer<typeof MyCredentialsSchema>;

/* ------------------------------------------------------------------------ */
/* Hub summary (avatar badge and Today cards)                                */
/* ------------------------------------------------------------------------ */

export const TODO_KINDS = [
  'credential_expired',
  'credential_due',
  'credential_missing',
  'ack_overdue',
  'ack_due',
  'training_overdue',
  'training_due',
  'ce_short',
  'checklist',
  'pto_pending',
] as const;

export const HubTodoSchema = z.object({
  id: z.string(),
  kind: z.enum(TODO_KINDS),
  title: z.string(),
  detail: z.string(),
  tone: ComplianceToneSchema,
  /** The My Hub tab that resolves it. */
  tab: z.enum(['today', 'schedule', 'time', 'licenses', 'training', 'documents']),
  dueOn: IsoDate.nullable(),
});
export type HubTodo = z.infer<typeof HubTodoSchema>;

export const HubSummarySchema = z.object({
  /** The avatar badge: items past due (expired, overdue sign-offs and training). */
  overdue: z.number().int().min(0),
  dueSoon: z.number().int().min(0),
  todos: z.array(HubTodoSchema),
  /** Managers only: people in their offices with an overdue item. */
  team: z
    .object({ peopleOverdue: z.number().int().min(0), pendingApprovals: z.number().int().min(0) })
    .nullable(),
});
export type HubSummary = z.infer<typeof HubSummarySchema>;

/* ------------------------------------------------------------------------ */
/* Staff documents and acknowledgements                                      */
/* ------------------------------------------------------------------------ */

export const HR_DOCUMENT_KINDS = [
  'handbook',
  'policy',
  'offer_letter',
  'job_description',
  'certificate',
  'training_evidence',
  'other',
] as const;
export const HrDocumentKindSchema = z.enum(HR_DOCUMENT_KINDS);
export type HrDocumentKind = z.infer<typeof HrDocumentKindSchema>;

export const HrDocumentVersionSchema = z.object({
  version: z.number().int().min(1),
  sha256: z.string(),
  mimeType: z.string(),
  sizeBytes: z.number().int(),
  filename: z.string().nullable(),
  hasText: z.boolean(),
  hasFile: z.boolean(),
  changeNote: z.string().nullable(),
  publishedByName: z.string(),
  publishedAt: Instant,
});
export type HrDocumentVersion = z.infer<typeof HrDocumentVersionSchema>;

export const HrDocumentSchema = z.object({
  id: Uuid,
  kind: HrDocumentKindSchema,
  title: z.string(),
  officeId: Uuid.nullable(),
  subjectUserId: Uuid.nullable(),
  requiresAck: z.boolean(),
  ackDueDays: z.number().int(),
  sensitivity: z.enum(['normal', 'sensitive']),
  currentVersion: z.number().int(),
  current: HrDocumentVersionSchema.nullable(),
  archived: z.boolean(),
  /** The caller's sign-off of the current version. */
  myAck: z.object({ version: z.number().int(), at: Instant }).nullable(),
  /** Day the current version must be signed by (requires_ack only). */
  ackDueOn: IsoDate.nullable(),
  ackTone: ComplianceToneSchema,
});
export type HrDocument = z.infer<typeof HrDocumentSchema>;

export const HrDocumentListSchema = z.object({ documents: z.array(HrDocumentSchema) });
export type HrDocumentList = z.infer<typeof HrDocumentListSchema>;

export const HrDocumentDetailSchema = HrDocumentSchema.extend({
  versions: z.array(HrDocumentVersionSchema),
  /** The current version's text (Markdown), when it has one. */
  bodyText: z.string().nullable(),
});
export type HrDocumentDetail = z.infer<typeof HrDocumentDetailSchema>;

export const CreateHrDocumentBody = z
  .object({
    kind: HrDocumentKindSchema,
    title: z.string().trim().min(1).max(200),
    officeId: Uuid.nullable().optional(),
    subjectUserId: Uuid.nullable().optional(),
    requiresAck: z.boolean().optional(),
    ackDueDays: z.number().int().min(0).max(365).optional(),
    sensitivity: z.enum(['normal', 'sensitive']).optional(),
    /** First version as text; a file is uploaded with POST /documents/:id/versions/file. */
    bodyText: z.string().min(1).max(400_000).optional(),
    changeNote: z.string().trim().max(500).optional(),
  })
  .strict();
export type CreateHrDocumentBody = z.infer<typeof CreateHrDocumentBody>;

export const TextVersionBody = z
  .object({
    bodyText: z.string().min(1).max(400_000),
    changeNote: z.string().trim().min(3).max(500),
  })
  .strict();
export type TextVersionBody = z.infer<typeof TextVersionBody>;

export const AcknowledgeBody = z
  .object({
    version: z.number().int().min(1),
    typedName: z.string().trim().min(2).max(120),
    /** The reader ticked "I have read and understand this version". */
    attest: z.literal(true),
  })
  .strict();
export type AcknowledgeBody = z.infer<typeof AcknowledgeBody>;

export const AckRosterSchema = z.object({
  documentId: Uuid,
  version: z.number().int(),
  dueOn: IsoDate.nullable(),
  people: z.array(
    z.object({
      userId: Uuid,
      name: z.string(),
      ackedVersion: z.number().int().nullable(),
      ackedAt: Instant.nullable(),
      typedName: z.string().nullable(),
      tone: ComplianceToneSchema,
    }),
  ),
});
export type AckRoster = z.infer<typeof AckRosterSchema>;

/* ------------------------------------------------------------------------ */
/* Compliance grid                                                           */
/* ------------------------------------------------------------------------ */

export const ComplianceColumnSchema = z.object({
  key: z.string(),
  label: z.string(),
  group: z.enum(['credential', 'ce', 'training', 'document', 'sensitive']),
});

export const ComplianceCellSchema = z.object({
  tone: ComplianceToneSchema,
  label: z.string(),
  /** Date that drives the tone (expiry, due day). */
  date: IsoDate.nullable(),
});
export type ComplianceCell = z.infer<typeof ComplianceCellSchema>;

export const ComplianceRowSchema = z.object({
  userId: Uuid,
  name: z.string(),
  title: z.string().nullable(),
  roles: z.array(z.string()),
  officeIds: z.array(Uuid),
  cells: z.record(z.string(), ComplianceCellSchema),
  worst: ComplianceToneSchema,
});
export type ComplianceRow = z.infer<typeof ComplianceRowSchema>;

export const ComplianceMatrixSchema = z.object({
  generatedAt: Instant,
  today: IsoDate,
  columns: z.array(ComplianceColumnSchema),
  rows: z.array(ComplianceRowSchema),
  counts: z.object({ overdue: z.number().int(), due: z.number().int(), soon: z.number().int() }),
});
export type ComplianceMatrix = z.infer<typeof ComplianceMatrixSchema>;

export const TeamListSchema = z.object({ people: z.array(StaffProfileSchema) });
export type TeamList = z.infer<typeof TeamListSchema>;

/* ------------------------------------------------------------------------ */
/* Time & pay                                                                */
/* ------------------------------------------------------------------------ */

/**
 * Where staff open their pay stubs, W-4 and direct deposit in Square (the Team
 * app shows the same). Unverified deep link: Square documents the place
 * (squareup.com/help/us/en/article/5537), not a stable URL; change it here.
 */
export const SQUARE_PAY_STUB_URL = 'https://squareup.com/dashboard/team/payroll';

export const TimePaySchema = z.object({
  /** False until the timeclock is installed on this server. */
  timeclockAvailable: z.boolean(),
  timezone: z.string(),
  period: z.object({
    start: IsoDate,
    end: IsoDate,
    frequency: z.enum(['weekly', 'biweekly', 'semimonthly', 'monthly']),
    /** Where the period came from: the Square Payroll connection or the default. */
    source: z.enum(['square', 'default']),
  }),
  days: z.array(z.object({ date: IsoDate, minutes: z.number().int() })),
  weeks: z.array(
    z.object({
      weekStart: IsoDate,
      minutes: z.number().int(),
      regularMinutes: z.number().int(),
      overtimeMinutes: z.number().int(),
    }),
  ),
  totals: z.object({
    minutes: z.number().int(),
    regularMinutes: z.number().int(),
    overtimeMinutes: z.number().int(),
  }),
  /** Entries open now, and ones left open long enough that a manager must close them. */
  openEntries: z.number().int(),
  flaggedEntries: z.number().int(),
  entries: z.array(
    z.object({
      id: Uuid,
      officeId: Uuid,
      clockedInAt: Instant,
      clockedOutAt: Instant.nullable(),
      outReason: z.enum(['end', 'break']).nullable(),
      minutes: z.number().int(),
    }),
  ),
  /** Plain-English caveats ("Square applies its own overtime rules"). */
  notes: z.array(z.string()),
  square: z.object({ payStubUrl: z.string(), connected: z.boolean() }),
});
export type TimePay = z.infer<typeof TimePaySchema>;

export const TimePayQuerySchema = z.object({
  /** 0 = the current pay period, 1 = the one before, ... */
  back: z.coerce.number().int().min(0).max(26).optional(),
});

/* ------------------------------------------------------------------------ */
/* Team & HR detail, alert log, document admin                               */
/* ------------------------------------------------------------------------ */

export const TeamMemberSchema = z.object({
  profile: StaffProfileSchema,
  credentials: MyCredentialsSchema,
  documents: z.array(HrDocumentSchema),
  todos: z.array(HubTodoSchema),
});
export type TeamMember = z.infer<typeof TeamMemberSchema>;

export const AlertLogSchema = z.object({
  /** True when the master switch lets staff emails out (false: every email is held). */
  emailGateOpen: z.boolean(),
  gateReason: z.string().nullable(),
  /**
   * What needs action now, from the compliance grid (one source: owner role
   * test, "38 overdue items, but Alerts said No alerts yet"): every overdue,
   * due and soon item, worst first.
   */
  open: z
    .array(
      z.object({
        userId: Uuid,
        name: z.string(),
        item: z.string(),
        tone: ComplianceToneSchema,
        label: z.string(),
        date: IsoDate.nullable(),
      }),
    )
    .default([]),
  openCounts: z
    .object({ overdue: z.number().int(), due: z.number().int(), soon: z.number().int() })
    .default({ overdue: 0, due: 0, soon: 0 }),
  alerts: z.array(
    z.object({
      id: Uuid,
      at: Instant,
      subjectName: z.string(),
      recipientName: z.string(),
      audience: z.enum(['staff', 'manager']),
      typeLabel: z.string(),
      days: z.number().int(),
      expiresOn: IsoDate,
      channel: z.enum(['in_app', 'email']),
      outcome: z.enum(['recorded', 'sent', 'held', 'skipped', 'failed']),
      reason: z.string().nullable(),
    }),
  ),
});
export type AlertLog = z.infer<typeof AlertLogSchema>;

export const AlertRunResultSchema = z.object({
  recorded: z.number().int(),
  emailed: z.number().int(),
  held: z.number().int(),
  skipped: z.number().int(),
});
export type AlertRunResult = z.infer<typeof AlertRunResultSchema>;

export const PatchHrDocumentBody = z
  .object({
    title: z.string().trim().min(1).max(200).optional(),
    requiresAck: z.boolean().optional(),
    ackDueDays: z.number().int().min(0).max(365).optional(),
    archived: z.boolean().optional(),
  })
  .strict();
export type PatchHrDocumentBody = z.infer<typeof PatchHrDocumentBody>;

export const FileVersionQuery = z.object({
  filename: z.string().trim().min(1).max(255),
  changeNote: z.string().trim().max(500).optional(),
});

export const IdResultSchema = z.object({ id: Uuid });

/* ------------------------------------------------------------------------ */
/* CE hours (owner role test: "CE hours ... arrive in a later release")      */
/* ------------------------------------------------------------------------ */

export const CeEntrySchema = z.object({
  id: Uuid,
  userId: Uuid,
  course: z.string(),
  provider: z.string().nullable(),
  completedOn: IsoDate,
  hours: z.number(),
  category: z.string().nullable(),
  note: z.string().nullable(),
  enteredByName: z.string().nullable(),
});
export type CeEntry = z.infer<typeof CeEntrySchema>;

export const CeEntryCreateSchema = z.object({
  /** Omitted: yourself (My Hub). A manager names the person (Team & HR). */
  userId: Uuid.optional(),
  course: z.string().trim().min(1).max(200),
  provider: z.string().trim().min(1).max(120).nullable().optional(),
  completedOn: IsoDate,
  hours: z.number().positive().max(100),
  category: z
    .string()
    .regex(/^[a-z][a-z0-9_]{1,40}$/)
    .nullable()
    .optional(),
  note: z.string().max(500).nullable().optional(),
});
export type CeEntryCreate = z.infer<typeof CeEntryCreateSchema>;

/** The CE cycle of the license that requires CE (null: no such license on file). */
export const CeCycleSchema = z.object({
  licenseKey: z.string(),
  licenseLabel: z.string(),
  from: IsoDate,
  to: IsoDate,
  requiredHours: z.number(),
  hours: z.number(),
  tone: ComplianceToneSchema,
  label: z.string(),
});
export type CeCycle = z.infer<typeof CeCycleSchema>;

export const CeSummarySchema = z.object({
  userId: Uuid,
  entries: z.array(CeEntrySchema),
  cycle: CeCycleSchema.nullable(),
  /** All hours on file, every cycle. */
  totalHours: z.number(),
});
export type CeSummary = z.infer<typeof CeSummarySchema>;
